import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  List,
  ThemeIcon,
  Box,
  Group,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconScale,
  IconLock,
  IconBrain,
  IconUsers,
  IconMessageCircle,
  IconEqual,
  IconAd,
  IconGavel,
  IconBook,
  IconReceipt,
} from "@tabler/icons-react";
import styles from "./SplashLanding.module.css";
import {Footer} from "./Footer/Footer";
import { useState } from "react";
import { Header } from "./Header/Header";
const guidelineCardStyles = {
  card: {
    height: "auto",
    minHeight: "320px",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  resourceLink: {
    color: "var(--primary-color)",
    textDecoration: "none",
    transition: "all 0.2s ease",
    borderBottom: "1px solid var(--primary-color)",
    "&:hover": {
      opacity: 0.8,
      borderBottom: "2px solid var(--primary-color)",
    },
  },
  disclaimer: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    padding: "1rem",
    marginTop: "2rem",
  },
};

const AIGuidelines = [
  {
    icon: IconLock,
    title: "Maintaining Client Confidentiality",
    description: (
      <>
        Lawyers are responsible for ensuring that their use of AI tools complies
        with their jurisdiction's rules on client confidentiality.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_6_confidentiality_of_information/?login"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 1.6(c)
        </Text>{" "}
        requires lawyers to make "reasonable efforts to prevent the inadvertent
        or unauthorized disclosure of, or unauthorized access to, information
        relating to the representation of a client."
      </>
    ),
    details: [
      "Implement appropriate safeguards to protect client information when using AI tools",
      "Regularly check the privacy policies of AI tools you use",
      "Consult your local bar association for specific guidance",
    ],
  },
  {
    icon: IconBrain,
    title: "Competence and AI Usage",
    description: (
      <>
        Lawyers must understand the capabilities and limitations of AI tools to
        fulfill their duty of technological competence.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_1_competence/comment_on_rule_1_1/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 1.1 [Comment 8]
        </Text>{" "}
        requires lawyers to provide competent representation, including keeping
        "abreast of changes in the law and its practice, including the benefits
        and risks associated with relevant technology."{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_2_1_advisor/#:~:text=In%20representing%20a%20client%2C%20a,relevant%20to%20the%20client's%20situation."
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 2.1
        </Text>{" "}
        requires a lawyer to "exercise independent professional judgment."
      </>
    ),
    details: [
      "Regularly assess and update AI competence",
      "Stay informed about relevant AI developments",
      "Remember AI tools supplement, not replace, professional judgment",
    ],
  },
  {
    icon: IconScale,
    title: "Avoiding Conflicts of Interest",
    description: (
      <>
        Use of AI tools does not absolve lawyers of their duty to identify and
        avoid conflicts of interest.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_7_conflict_of_interest_current_clients/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 1.7
        </Text>{" "}
        prohibits representation involving a concurrent conflict of interest,
        which exists if: (1) representing one client would be directly adverse
        to another client, or (2) there is a significant risk that
        representation would be materially limited by responsibilities to
        another client, former client, third person, or personal interests.
      </>
    ),
    details: [
      "Ensure that your conflict checking procedures account for AI-assisted work",
      "Document AI tool usage in conflict checks",
      "When in doubt, consult your jurisdiction's ethical guidelines on conflicts of interest",
    ],
  },
  {
    icon: IconUsers,
    title: "Responsibilities Regarding Nonlawyer Assistance",
    description: (
      <>
        Lawyers remain responsible for supervising the use of AI tools, as they
        would any other form of technology or support staff.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_5_3_responsibilities_regarding_nonlawyer_assistant/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 5.3
        </Text>{" "}
        extends a lawyer's responsibility to non-lawyer assistance, which would
        include AI tools. It requires lawyers to make "reasonable efforts to
        ensure that the firm has in effect measures giving reasonable assurance
        that the person's conduct is compatible with the professional
        obligations of the lawyer."
      </>
    ),
    details: [
      "Implement appropriate oversight measures to ensure AI outputs align with professional standards",
      "Refer to your jurisdiction's rules on the supervision of non-lawyer assistance for guidance",
    ],
  },
  {
    icon: IconMessageCircle,
    title: "Duty of Communication",
    description: (
      <>
        Lawyers should consider their obligation to communicate with clients
        about the use of AI tools in their representation.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_4_communications/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 1.4
        </Text>{" "}
        requires lawyers to "explain a matter to the extent reasonably necessary
        to permit the client to make informed decisions regarding the
        representation."
      </>
    ),
    details: [
      "Inform clients about the use of AI in their matters when appropriate",
      "Explain the benefits and potential risks of using AI tools in the representation",
      "Obtain client consent when necessary, especially if the use of AI tools might affect the cost or outcome of the representation",
    ],
  },
  {
    icon: IconEqual,
    title: "Fairness and Non-Discrimination",
    description: (
      <>
        Lawyers must ensure that their use of AI tools does not result in unfair
        bias or discrimination.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_8_4_misconduct/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 8.4(g)
        </Text>{" "}
        prohibits conduct that the lawyer knows or reasonably should know is
        harassment or discrimination based on various protected characteristics.
      </>
    ),
    details: [
      "Be aware of potential biases in AI algorithms and take steps to mitigate them",
      "Regularly audit AI-generated content for potential discriminatory language or outcomes",
    ],
  },
  {
    icon: IconAd,
    title: "Advertising and Solicitation",
    description: (
      <>
        Lawyers must ensure that any claims about AI capabilities in their
        marketing materials are accurate and not misleading.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_7_1_communication_concerning_a_lawyer_s_services/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 7.1
        </Text>{" "}
        prohibits false or misleading communications about the lawyer or the
        lawyer's services.
      </>
    ),
    details: [
      "Avoid overstating the capabilities or benefits of AI in marketing materials",
      "Clearly communicate the role of AI in legal services to potential clients",
      "Ensure that any comparisons between AI-enhanced services and traditional services are fair and substantiated",
    ],
  },
  {
    icon: IconGavel,
    title: "Unauthorized Practice of Law",
    description: (
      <>
        Lawyers must ensure that their use of AI tools does not inadvertently
        facilitate the unauthorized practice of law.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_5_5_unauthorized_practice_of_law_multijurisdictional_practice_of_law/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 5.5(a)
        </Text>{" "}
        states that a lawyer shall not practice law in a jurisdiction in
        violation of the regulation of the legal profession in that
        jurisdiction.
      </>
    ),
    details: [
      "Ensure that AI tools are used to assist, not replace, the lawyer's professional judgment",
      "Implement safeguards to prevent AI systems from providing direct legal advice to clients without lawyer oversight",
      "Be cautious about using AI tools across jurisdictions where the lawyer is not licensed to practice",
    ],
  },
  {
    icon: IconBook,
    title: "Professional Self-Regulation",
    description: (
      <>
        Lawyers have a responsibility to ensure that the use of AI in the legal
        profession maintains the integrity of the legal system.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_8_3_reporting_professional_misconduct/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 8.3
        </Text>{" "}
        requires lawyers to report professional misconduct of other lawyers that
        raises a substantial question as to those lawyers' honesty,
        trustworthiness, or fitness as lawyers.
      </>
    ),
    details: [
      "Stay informed about best practices and ethical guidelines for AI use in law",
      "Participate in professional discussions and initiatives regarding the ethical use of AI in legal practice",
      "Report any observed misuse of AI tools that could compromise the integrity of the legal profession",
    ],
  },
  {
    icon: IconReceipt,
    title: "Billing Practices",
    description: (
      <>
        Ensure your billing practices for AI-assisted work comply with your
        jurisdiction's rules on reasonable fees.{" "}
        <Text
          component="a"
          href="https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/rule_1_5_fees/"
          target="_blank"
          style={guidelineCardStyles.resourceLink}
        >
          Model Rule 1.5(a)
        </Text>{" "}
        requires that a lawyer's fee be reasonable.
      </>
    ),
    details: [
      "Consider whether and how to disclose AI use in billing statements",
      "Regularly review and adjust your billing practices to reflect evolving standards and practices",
    ],
  },
];

const responsiveStyles = {
  "@media (max-width: 48em)": {
    ".mantine-Paper-root": {
      width: "100% !important",
    },
    ".mantine-Title-root": {
      fontSize: "1.5rem !important",
    },
    ".mantine-Text-root": {
      fontSize: "0.9rem !important",
    },
    ".mantine-Container-root": {
      padding: "1rem !important",
    },
    ".mantine-Group-root": {
      gap: "1rem !important",
    },
  },
};
const resourceCardStyles = {
  card: {
    height: "auto",
    minHeight: "320px",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  resourceLink: {
    color: "var(--primary-color)",
    textDecoration: "none",
    transition: "all 0.2s ease",
    borderBottom: "1px solid var(--primary-color)",
    "&:hover": {
      opacity: 0.8,
      borderBottom: "2px solid var(--primary-color)",
    },
  },
  disclaimer: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    padding: "1rem",
    marginTop: "2rem",
  },
};
// Add a new component for the resources section
const ResourcesSection = () => (
  <Paper withBorder p="xl" radius="md">
    <Stack spacing="md">
      <Title order={2}>Additional Resources</Title>
      <List>
        <List.Item>
          <Text
            component="a"
            href="https://www.americanbar.org/news/abanews/aba-news-archives/2024/07/aba-issues-first-ethics-guidance-ai-tools/"
            target="_blank"
            style={resourceCardStyles.resourceLink}
          >
            ABA Issues First Ethics Guidance on Lawyers' Use of AI Tools (July
            2024)
          </Text>
          <Text size="sm" mt="xs" color="dimmed">
            Formal Opinion 512 states that to ensure clients are protected,
            lawyers and law firms using Generative AI must "fully consider their
            applicable ethical obligations," which includes duties to provide
            competent legal representation, to protect client information, to
            communicate with clients and to charge reasonable fees consistent
            with time spent using Generative AI.
          </Text>
        </List.Item>
        <List.Item mt="md">
          <Text weight={500}>State Bar Association Guidelines:</Text>
          <List withPadding ml="md" mt="xs">
            {[
              {
                title:
                  "Pennsylvania Bar Association Ethics Opinion 2024-200 (May 2024)",
                url: "https://www.pabar.org/ethics/2024-200",
              },
              {
                title:
                  "New York State Bar Association Task Force Report on Artificial Intelligence (April 2024)",
                url: "https://nysba.org/ai-task-force-report-2024",
              },
              {
                title: "Florida Bar Ethics Opinion 24-1 (January 2024)",
                url: "https://www.floridabar.org/etopinions/opinion-24-1/",
              },
              {
                title: "New Jersey Lawyers' AI Usage Guidelines (January 2024)",
                url: "https://www.njcourts.gov/lawyers/ai-guidelines",
              },
              {
                title:
                  "California Bar Association Generative AI Practical Guidance (November 2023)",
                url: "https://www.calbar.ca.gov/Portals/0/documents/ethics/Generative-AI-Practical-Guidance.pdf",
              },
              {
                title: "Michigan Bar Ethics Opinion JI-155 (October 2023)",
                url: "https://www.michbar.org/opinions/ethics/numbered_opinions/JI-155",
              },
            ].map((resource, idx) => (
              <List.Item key={idx}>
                <Text
                  component="a"
                  href={resource.url}
                  target="_blank"
                  style={resourceCardStyles.resourceLink}
                >
                  {resource.title}
                </Text>
              </List.Item>
            ))}
          </List>
        </List.Item>
        <List.Item mt="md">
          <Text
            component="a"
            href="https://www.uscourts.gov/ai-guidelines"
            target="_blank"
            style={resourceCardStyles.resourceLink}
          >
            Table of court orders, local rules, and guidelines on AI use (March
            2024)
          </Text>
        </List.Item>
      </List>
    </Stack>
  </Paper>
);
// Add a new component for the disclaimer
const DisclaimerSection = () => (
  <Paper withBorder p="md" radius="md" style={resourceCardStyles.disclaimer}>
    <Text size="sm">
      <strong>DISCLAIMER:</strong> This document is for informational purposes
      only and does not constitute legal advice. Lawyers are responsible for
      staying informed about the current rules and guidelines in their
      jurisdiction and for exercising their professional judgment in applying
      these principles to their use of AI tools. Always consult with your local
      bar association, ethics counsel, or appropriate regulatory body for
      up-to-date and jurisdiction-specific guidance on professional
      responsibility in the context of AI use in legal practice.
    </Text>
  </Paper>
);

const linkStyles = {
  a: {
    color: "var(--primary-color)",
    textDecoration: "none",
    transition: "all 0.2s ease",
    borderBottom: "1px solid var(--primary-color)",

    "&:hover": {
      opacity: 0.8,
      borderBottom: "2px solid var(--primary-color)",
    },
  },
  ".logo-link": {
    border: "none",
    "&:hover": {
      border: "none",
      opacity: 1,
    },
  },
};

export default function ResponsibleAI() {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [opened, setOpened] = useState(false);
  const toggle = () => setOpened((o) => !o);

  const GuidelineCard = ({ guideline }) => (
    <Paper
      w={isMobile ? "100%" : "380px"}
      withBorder
      p="md"
      radius="md"
      style={{ height: "auto", minHeight: "320px" }}
    >
      <Stack spacing="xs">
        <Group>
          <ThemeIcon size={40} radius="md">
            <guideline.icon size={24} />
          </ThemeIcon>
          <Title order={3}>{guideline.title}</Title>
        </Group>
        <Text size="sm">{guideline.description}</Text>
        <List size="sm" spacing="xs">
          {guideline.details.map((detail, idx) => (
            <List.Item key={idx}>{detail}</List.Item>
          ))}
        </List>
      </Stack>
    </Paper>
  );

  return (
    <Box
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {Header()}
      <Box styles={linkStyles}>
        <Container size="lg" px={isMobile ? "xs" : "md"} py="xl">
          <Stack spacing="xl">
            <Title order={1} align="center">
              AI Ethics & Professional Responsibility Guide
            </Title>
            <Text size="lg" align="center" maw={800} mx="auto">
              AI use by lawyers implicates several provisions of the ABA Model
              Rules of Professional Conduct, which serve as the foundation for
              ethical rules across many U.S. jurisdictions. These model rules
              provide a framework for understanding the ethical obligations that
              arise when implementing AI in legal practice.
            </Text>

            <Group justify="center" gap="md" wrap="wrap">
              {AIGuidelines.map((guideline, index) => (
                <GuidelineCard key={index} guideline={guideline} />
              ))}
            </Group>

            {/* <TechnologyToolsSection /> */}
            <ResourcesSection />
            <DisclaimerSection />
          </Stack>
        </Container>
      </Box>
      {Footer()}
    </Box>
  );
}
